/** The order-search namespaces wired to their autocomplete fetchers.
 *
 * Two families (see OrderFilterNode in the OpenAPI spec):
 *  - ORDER-LEVEL leaves filter the Order row directly (no `allAny`).
 *  - BOOKING-LEVEL leaves (`allAny: true`) match the order's bookings via an
 *    Exists() and honour the any/all `*` scope modifier — `any` (default) = the
 *    order has ≥1 matching booking, `all` = every booking matches.
 *
 * Operators per namespace are a SUBSET of the spec's
 * `OrderFilterNode.x-namespace-operators` (contract-tested). `exact` is
 * deliberately omitted on `tour`/`user` even though the backend accepts it —
 * the DSL serialises `exact` and `iexact` both to `=`, which re-parses to
 * `iexact`, so an `exact` chip couldn't round-trip through the URL. ID pinning
 * goes through the backend's all-digits `iexact` fallback instead (same rule
 * as bookingsNamespaces).
 *
 * The `allAny` set here must exactly equal the spec's
 * `x-booking-level-namespaces` (also contract-tested).
 */
import type { NamespaceConfigMap } from '@/components/QuerySearch';
import { makeSuggestionFetcher } from '@/api/orders';

import { BOOKING_STATE_LABELS } from '../bookings/bookingsNamespaces';

/** The Order-level booking_state labels (Order.BOOKING_STATES) — a small fixed
 *  enum, so `order_state` suggestions are filtered locally with no API call.
 *  Distinct from the booking-level `state` namespace's BOOKING_STATE_LABELS. */
export const ORDER_STATE_LABELS = [
  'On hold',
  'Confirmed',
  'Cancelled',
  'Cancelled on hold',
  'Expired',
  'Pending confirmation',
];

/** Build a local (no-API) suggestion fetcher from a fixed label list. */
function localLabels(labels: string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

export function buildOrderNamespaces(): NamespaceConfigMap {
  return {
    // ---- ORDER-LEVEL (no allAny) -----------------------------------------
    order: {
      label: 'order',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Order reference from the booking system.',
      examples: ['order=ORD-100', 'order=~ORD-1'],
    },
    reseller: {
      label: 'reseller',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Reseller reference.',
      examples: ['reseller=RES-1', 'reseller=~RES'],
    },
    supplier: {
      label: 'supplier',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Supplier reference.',
      examples: ['supplier=SUP-1', 'supplier=~SUP'],
    },
    integration: {
      label: 'integration',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Integration / booking system the order came from.',
      examples: ['integration=bokun', 'integration=~ventrata'],
    },
    custom: {
      label: 'custom',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: "Order's machine ID in the source booking system (custom_id).",
      examples: ['custom=8f3c-1a2b', 'custom=~8f3c'],
    },
    contact_email: {
      label: 'contact_email',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: "Order contact email (the order's own contact, not a guest).",
      examples: ['contact_email=jane@example.com', 'contact_email=~@gmail.com'],
    },
    active: {
      label: 'active',
      operators: ['exact'],
      valueType: 'string',
      fetchSuggestions: localLabels(['yes', 'no']),
      help: 'Whether the order is active (not soft-hidden) — yes/no.',
      examples: ['active=no'],
    },
    order_state: {
      label: 'order_state',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(ORDER_STATE_LABELS),
      help: 'Order state, by label or raw integer value.',
      examples: ['order_state=confirmed', 'order_state="on hold"'],
    },
    tenant: {
      label: 'tenant',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tenant'),
      help: 'Tour operator (tenant) by code or name.',
      examples: ['tenant=curatedplanet', 'tenant=~mariott'],
    },
    // created/updated: exact = a whole calendar day; range bounds are precise
    // date-time values (bare dates still parse and span the whole day).
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the order record was created, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['created=2026-07-01', 'created="2026-07-01 09:00".."2026-07-08 18:00"'],
    },
    updated: {
      label: 'updated',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the order record was last updated, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['updated=2026-07-08', 'updated="2026-07-01 09:00".."2026-07-08 18:00"'],
    },

    // ---- BOOKING-LEVEL (allAny: true) ------------------------------------
    tour: {
      label: 'tour',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tour'),
      allAny: true,
      help: 'Tour / product name (matches the order’s bookings).',
      examples: ['tour=~sunset', 'tour=*"City Walking Tour"'],
    },
    date: {
      label: 'date',
      operators: ['exact', 'range'],
      valueType: 'date',
      dateFormat: 'yyyy-MM-dd',
      allAny: true,
      help: 'Booking date within the order. Use a range (from..to) to span a period.',
      examples: ['date=2024-06-01', 'date=2024-06-01..2024-06-30'],
    },
    departure: {
      label: 'departure',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tag', { category: 'departure' }),
      extraFilterParams: { category: 'departure' },
      allAny: true,
      help: 'Departure label / time slot (a "departure"-category tag).',
      examples: ['departure=08:00', 'departure="Morning slot"'],
    },
    state: {
      label: 'state',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(BOOKING_STATE_LABELS),
      allAny: true,
      help: 'Booking state within the order, by label or raw integer value.',
      examples: ['state=booked', 'state="on hold"'],
    },
    // Range folds into the VALUE ('2-4', '9+') rather than the DSL's `..` range
    // operator — matches the bookings Pax semantics.
    pax: {
      label: 'pax',
      operators: ['exact'],
      valueType: 'string',
      allAny: true,
      help: 'People count on a booking: a single value, an inclusive range, or open-ended.',
      examples: ['pax=2', 'pax=2-4', 'pax=9+'],
    },
    tag: {
      label: 'tag',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tag'),
      allAny: true,
      help: 'Booking tag by name. Pick "by ID" on a suggestion to match one exact tag row (ignores same-named variants).',
      examples: ['tag=~mariott', 'tag=*dinner', 'tag=#906'],
    },
    category: {
      label: 'category',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('category'),
      allAny: true,
      help: 'Tag category code (groups tags — e.g. "departure", "checkin"). Pick "by ID" to match one exact category.',
      examples: ['category=departure', 'category=~check'],
    },
    // CP-8095 — the ORDER's own tags. Order-level, so no `allAny`: there is no
    // per-booking any/all to quantify over.
    order_tag: {
      label: 'order_tag',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tag'),
      help: 'Tag on the ORDER itself (CP-8095) — `tag` searches its bookings instead. Pick "by ID" on a suggestion to match one exact tag row.',
      examples: ['order_tag=corporate', 'order_tag=~corp', 'order_tag=#906'],
    },
    order_category: {
      label: 'order_category',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('category'),
      help: 'Tag category of a tag on the ORDER itself (CP-8095). Pick "by ID" to match one exact category.',
      examples: ['order_category=market', 'order_category=~mark'],
    },
    // Reached through the order's bookings, so these take the any/all `*`.
    user_tag: {
      label: 'user_tag',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tag'),
      allAny: true,
      help: 'Tag on the USER ACCOUNT behind a booking of this order (CP-8095).',
      examples: ['user_tag=part-time', 'user_tag=*part-time', 'user_tag=#906'],
    },
    user_category: {
      label: 'user_category',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('category'),
      allAny: true,
      help: 'Tag category of a tag on the USER ACCOUNT behind a booking of this order (CP-8095).',
      examples: ['user_category=staff_type', 'user_category=*staff_type'],
    },
    tour_tag: {
      label: 'tour_tag',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tag'),
      allAny: true,
      help: 'Tag on the TOUR of a booking of this order (CP-8095).',
      examples: ['tour_tag=winter', 'tour_tag=*winter', 'tour_tag=#906'],
    },
    tour_category: {
      label: 'tour_category',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('category'),
      allAny: true,
      help: 'Tag category of a tag on the TOUR of a booking of this order (CP-8095).',
      examples: ['tour_category=market', 'tour_category=*market'],
    },
    name: {
      label: 'name',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('user'),
      allAny: true,
      help: 'Guest first or last name. Quote values with spaces.',
      examples: ['name=~smith', 'name="John Smith"'],
    },
    email: {
      label: 'email',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('user'),
      allAny: true,
      help: 'Guest email address (exact or contains).',
      examples: ['email=jane@example.com', 'email=~@gmail.com'],
    },
    phone: {
      label: 'phone',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('user'),
      allAny: true,
      help: 'Guest phone number.',
      examples: ['phone=+15551234567', 'phone=~555'],
    },
    user: {
      label: 'user',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('user'),
      allAny: true,
      help: 'Guest by email or phone (matches either identifier). Pick "by ID" to match one exact guest.',
      examples: ['user=jane@example.com', 'user=~jane'],
    },
    // id is an opaque, near-unique external-system identifier — no bounded
    // suggestable set, so (unlike the namespaces above) it has no fetchSuggestions.
    id: {
      label: 'id',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      allAny: true,
      help: "Booking's ID in the source booking system: its custom_id, or its second ID there (alt id, e.g. Ventrata's native id).",
      examples: ['id=CID-12345', 'id=~CID-123'],
    },
  };
}
